// ABOUTME: Full-page transformation schema editor for one config, reached from the config Edit drawer
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, PackageOpen } from 'lucide-react';
import { configsApi } from '../api/adminApi';
import { useUpdateConfigSchema } from '../hooks/useAdminQueries';
import { SchemaMapper } from '../components/SchemaMapper/SchemaMapper';
import { LoadingCard } from '../components/admin/LoadingCard';
import { Button } from '../components/ui/button';
import { clientTransformationPathConfig } from '../../../transmormer/globals/metadata-registry';
import { Source, Destination, EntityType } from '../../../types/enums';
import JsonSerializer from '../../../utilities/jsonSerializer';

export default function ConfigSchemaPage() {
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const updateSchema = useUpdateConfigSchema();

  const { data: config, isLoading, error } = useQuery({
    queryKey: ['config', configId],
    queryFn: () => configsApi.getById(configId!),
    enabled: !!configId,
  });

  const goBack = () => navigate('/admin');

  if (isLoading) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <LoadingCard />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Button variant="outline" onClick={goBack}>
          <ChevronLeft size={16} /> Back
        </Button>
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-medium)' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 500, marginBottom: '0.5rem' }}>Config Not Found</h3>
          <p>The requested config could not be loaded.</p>
        </div>
      </div>
    );
  }

  const pathConfig = clientTransformationPathConfig(
    config.origin as Source,
    config.destination as Destination,
    config.entity as EntityType,
  );

  if (!pathConfig) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Button variant="outline" onClick={goBack}>
          <ChevronLeft size={16} /> Back
        </Button>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem',
          textAlign: 'center',
          color: 'var(--text-medium)'
        }}>
          <PackageOpen size={64} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 500, marginBottom: '0.5rem' }}>Configuration Not Found</h3>
          <p>No transformation configuration found for this path.</p>
        </div>
      </div>
    );
  }

  // A config with no stored schema starts from the path's default mapping.
  const initialSchema =
    config.schema ?? JsonSerializer.serialize(pathConfig.schema);

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <Button variant="outline" onClick={goBack}>
          <ChevronLeft size={16} /> Back
        </Button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
          Schema Editor — {config.origin} → {config.destination} ({config.entity})
        </h1>
      </div>
      <SchemaMapper
        sourceZod={pathConfig.inputZod}
        destinationZod={pathConfig.outputZod}
        sampleData={pathConfig.mockData}
        schema={initialSchema}
        onSave={async (serializedSchema) => {
          await updateSchema.mutateAsync({
            id: configId!,
            schema: serializedSchema,
          });
        }}
      />
    </div>
  );
}
